import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useApi } from '../Api';
import { BrokerageCustodianFormDTO } from '../types/BrokerageCustodian';
import CustomInput from '../controls/CustomInput';
import { FormSelect } from '../controls/FormSelect';

const BrokerageCustodianFormPage: React.FC = () => {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const api = useApi();

  const [formData, setFormData] = useState<BrokerageCustodianFormDTO>({
    id: 0,
    businessName: '',
    businessType: '',
    assetsClass: '',
    businessWebsite: '',
    businessEmail: '',
    businessPhone: '',
    businessLocationAddressLine1: '',
    businessLocationAddressLine2: '',
    businessLocationAddressCity: '',
    businessLocationAddressState: '',
    businessLocationAddressZipCode: '',
    businessLocationAddressCountry: '',
    contactNotes: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    const loadBrokerageCustodian = async (brokerageCustodianId: number) => {
      try {
        setIsLoading(true);
        const response = await api.get(`/api/brokeragecustodian/getbrokeragecustodian?id=${brokerageCustodianId}`);
        setFormData(response.data.brokerageCustodian);
      } catch (error) {
        console.error('Error loading brokerageCustodian:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Check if we're editing an existing brokerageCustodian
    const brokerageCustodianId = new URLSearchParams(location.search).get('brokerageCustodianId');
    if (brokerageCustodianId) {
      setIsEditing(true);
      loadBrokerageCustodian(parseInt(brokerageCustodianId));
    }
  }, [location.search, api]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!familyId) return;

    try {
      setIsLoading(true);

      if (isEditing) {
        await api.put(`/api/brokeragecustodian/updatebrokeragecustodian`, formData);
      } else {
        await api.post(`/api/brokeragecustodian/createbrokeragecustodian?familyId=${familyId}`, formData);
      }
      navigate(`/family-management/${familyId}`);
    } catch (error) {
      console.error('Error saving brokerageCustodian:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    if (familyId) {
      navigate(`/family-management/${familyId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-md">
            <div className="px-6 py-8">
              <h1 className="text-3xl font-bold mb-2">
                {isEditing ? 'Edit Brokerage/Custodian' : 'Add New Brokerage/Custodian'}
              </h1>
              <p className="text-lg">
                {isEditing ? 'Update Brokerage/Custodian information' : 'Enter Brokerage/Custodian information to add to the family'}
              </p>
            </div>
          </div>

          {/* Basic Information Card */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <CustomInput
                label="Business Name"
                name="businessName"
                value={formData.businessName}
                onChange={(val) => setFormData(prev => ({ ...prev, businessName: val }))}
                required
              />

              <FormSelect
                label="Business Type"
                value={formData.businessType || null}
                options={[
                  { value: 'Brokerage', label: 'Brokerage' },
                  { value: 'Custodian', label: 'Custodian' },
                ]}
                onChange={(val) => setFormData(prev => ({ ...prev, businessType: (val as string) ?? '' }))}
                required
              />

              <CustomInput
                label="Assets Class"
                name="assetsClass"
                value={formData.assetsClass}
                onChange={(val) => setFormData(prev => ({ ...prev, assetsClass: val }))}
                required
              />

              <CustomInput
                label="Business Website"
                name="businessWebsite"
                value={formData.businessWebsite}
                onChange={(val) => setFormData(prev => ({ ...prev, businessWebsite: val }))}
                required
              />

              <CustomInput
                label="Business Email"
                name="businessEmail"
                type="email"
                value={formData.businessEmail}
                onChange={(val) => setFormData(prev => ({ ...prev, businessEmail: val }))}
                required
              />

              <CustomInput
                label="Business Phone"
                name="businessPhone"
                type="tel"
                value={formData.businessPhone}
                onChange={(val) => setFormData(prev => ({ ...prev, businessPhone: val }))}
                required
              />

              <CustomInput
                label="Address Line 1"
                name="businessLocationAddressLine1"
                value={formData.businessLocationAddressLine1}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressLine1: val }))}
                required
              />

              <CustomInput
                label="Address Line 2"
                name="businessLocationAddressLine2"
                value={formData.businessLocationAddressLine2}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressLine2: val }))}
              />

              <CustomInput
                label="City"
                name="businessLocationAddressCity"
                value={formData.businessLocationAddressCity}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressCity: val }))}
                required
              />

              <CustomInput
                label="State"
                name="businessLocationAddressState"
                value={formData.businessLocationAddressState}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressState: val }))}
                required
              />

              <CustomInput
                label="Zip Code"
                name="businessLocationAddressZipCode"
                value={formData.businessLocationAddressZipCode}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressZipCode: val }))}
                required
              />

              <CustomInput
                label="Country"
                name="businessLocationAddressCountry"
                value={formData.businessLocationAddressCountry}
                onChange={(val) => setFormData(prev => ({ ...prev, businessLocationAddressCountry: val }))}
                required
              />

              <CustomInput
                label="Contact Notes"
                name="contactNotes"
                value={formData.contactNotes}
                onChange={(val) => setFormData(prev => ({ ...prev, contactNotes: val }))}
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary"
            >
              {isLoading ? 'Saving...' : (isEditing ? 'Update Brokerage/Custodian' : 'Add Brokerage/Custodian')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default BrokerageCustodianFormPage;
